import { navigate } from '../core/router';
import { assignableRoles, currentRole } from '../core/access';
import { appState } from '../core/state';
import { esc, setPageTitle, showErr, roleLabel, } from './users-shared';
import { fetchTeams } from './teams-shared';
import { t } from '../ui/lang';
/**
 * Full-page add/edit user form (replaces the old modals). Rendered for the
 * `#!/users/new` and `#!/users/<id>/edit` routes. Admin-only (route-guarded).
 */
export async function renderUserForm(mode, userId) {
    setPageTitle(mode === 'new' ? t('users.add_user_title') : t('users.edit_user_title'));
    const content = document.getElementById('content');
    content.innerHTML = `
        <div class="farm-devs-header">
            <button class="back-btn" id="uf-back">${t('users.back_to_users')}</button>
            <h2 class="settings-title" id="uf-title">${mode === 'new' ? t('users.add_user_title') : t('users.edit_user_title')}</h2>
        </div>
        <div id="uf-body" class="user-form-page"><p class="empty">${t('common.loading')}</p></div>
    `;
    content.querySelector('#uf-back').addEventListener('click', () => navigate({ page: 'users' }));
    const body = content.querySelector('#uf-body');
    // The group control is editable only for a superadmin actor. An admin's group
    // is inherited/immutable, so it's shown as read-only text and never submitted.
    const isSuperadmin = currentRole() === 'superadmin';
    // Load all teams (superadmin only — for the picker) and, when editing, the user.
    let availableTeams = [];
    let user;
    let allUsers = [];
    try {
        if (isSuperadmin)
            availableTeams = await fetchTeams();
        if (mode === 'edit') {
            const usersRes = await fetch('/api/users');
            if (!usersRes.ok)
                throw new Error();
            allUsers = await usersRes.json();
            user = allUsers.find((u) => u.id === userId);
            if (!user) {
                body.innerHTML = `<p class="empty">${t('users.user_not_found')}</p>`;
                return;
            }
        }
    }
    catch {
        body.innerHTML = `<p class="empty">${t('common.failed_to_load')}</p>`;
        return;
    }
    if (mode === 'edit' && user) {
        content.querySelector('#uf-title').textContent =
            t('users.edit_user_with_login', { login: user.login });
    }
    const credFields = mode === 'new'
        ? `
            <div class="modal__field">
                <label>${t('users.login')}</label>
                <input id="uf-login" type="text" autocomplete="off" placeholder="${t('users.login_placeholder')}" required />
            </div>
            <div class="modal__field">
                <label>${t('users.password')} <span class="modal__hint">${t('users.password_hidden_hint')}</span></label>
                <input id="uf-password" type="password" autocomplete="new-password" placeholder="••••••••" required />
            </div>
            <div class="modal__field">
                <label>${t('users.confirm_password')}</label>
                <input id="uf-password2" type="password" autocomplete="new-password" placeholder="••••••••" required />
            </div>`
        : `
            <div class="modal__field">
                <label>${t('users.new_password')} <span class="modal__hint">${t('users.new_password_hint')}</span></label>
                <input id="uf-password" type="password" autocomplete="new-password" placeholder="••••••••" />
            </div>
            <div class="modal__field">
                <label>${t('users.confirm_password')}</label>
                <input id="uf-password2" type="password" autocomplete="new-password" placeholder="••••••••" />
            </div>
            <p class="modal__hint">${t('users.new_password_resets_2fa')}</p>`;
    const role = user?.role ?? 'operator';
    // Offer only roles at or below the current user's rank (server enforces too).
    // Include the target's existing role so an edit never silently changes it. When
    // editing your OWN user this naturally means "current role or lower" — a user may
    // step their own role DOWN but never up (assignableRoles() caps at the actor's own
    // rank, so no higher role is ever offered; the server also blocks self-promotion).
    const options = assignableRoles();
    if (!options.includes(role))
        options.push(role);
    const roleOptions = options
        .map((r) => `<option value="${r}" ${role === r ? 'selected' : ''}>${roleLabel(r)}</option>`)
        .join('');
    // The Role field is frozen (shown as read-only text, not an editable <select>) when
    // the role cannot change — the server would reject it anyway, so don't offer it:
    //  • role_locked — a superadmin pinned this user's role (only a superadmin can lift it
    //    via the toggle below; while set nobody may change the role);
    //  • last superadmin — the lockout guard forbids demoting the final superadmin.
    // A hidden input keeps #uf-role.value present so the submit handler is unchanged.
    const currentLocked = user?.role_locked ?? false;
    const isLastSuperadmin = role === 'superadmin'
        && allUsers.filter((u) => u.role === 'superadmin').length <= 1;
    const roleFrozen = mode === 'edit' && (currentLocked || isLastSuperadmin);
    const roleControl = roleFrozen
        ? `<p class="modal__text uf-role-readonly">${roleLabel(role)}</p>
           <input type="hidden" id="uf-role" value="${role}" />`
        : `<select id="uf-role">${roleOptions}</select>`;
    // Superadmin-only «Lock role» toggle: when set, nobody (not even a superadmin) can
    // change this user's role until a superadmin clears it. Hidden for non-superadmins.
    const lockControl = isSuperadmin
        ? `
            <div class="modal__field modal__field--toggle">
                <label>${t('users.lock_role')}</label>
                <label class="toggle">
                    <input id="uf-role-locked" type="checkbox" ${currentLocked ? 'checked' : ''} />
                    <span class="toggle__track"></span>
                </label>
            </div>`
        : '';
    // Team control: superadmin → editable <select> (with a placeholder first option,
    // preselecting the user's current team_id); admin → read-only text only. The whole
    // field is hidden when the selected role is superadmin (superadmins are global,
    // never grouped); the wrapper carries an id so the role <select> can toggle it.
    // A team is MANDATORY for every other role — the placeholder is not a valid choice
    // (submit validates it, and the server rejects an ungrouped non-superadmin too).
    const currentTeamId = user?.team_id ?? null;
    const hideGroup = role === 'superadmin';
    let groupInner;
    if (isSuperadmin) {
        // A user of a SOFT-DELETED team keeps that team (the delete only hides the group),
        // but the picker lists live teams only — so carry the current one in as an extra,
        // flagged option. Without it the select would fall back to the placeholder and the
        // next save would silently un-group the user.
        const deletedCurrent = currentTeamId !== null && !availableTeams.some((x) => x.id === currentTeamId)
            ? `<option value="${currentTeamId}" selected>${t('users.team_deleted_option', { name: esc(user?.team_name ?? String(currentTeamId)) })}</option>`
            : '';
        const teamOptions = deletedCurrent + availableTeams
            .map((x) => `<option value="${x.id}" ${currentTeamId === x.id ? 'selected' : ''}>${esc(x.name)}</option>`)
            .join('');
        groupInner = `
            <label>${t('common.team')}</label>
            <select id="uf-team">
                <option value="" ${currentTeamId === null ? 'selected' : ''}>${t('users.select_team')}</option>
                ${teamOptions}
            </select>`;
    }
    else {
        // Admin actor: team is read-only. When creating, the server inherits the
        // admin's own team, so show that (currentUserTeamName) rather than a
        // misleading "(no team)". When editing, show the target user's team.
        const teamText = (mode === 'new'
            ? appState.currentUserTeamName
            : user?.team_name) ?? t('users.no_team');
        groupInner = `
            <label>${t('common.team')}</label>
            <p class="modal__text uf-team-readonly">${esc(teamText)}</p>`;
    }
    const groupControl = `
        <div class="modal__field" id="uf-team-field"${hideGroup ? ' style="display:none"' : ''}>
            ${groupInner}
        </div>`;
    // Generate a strong password + copy the credentials (panel URL / login / one-time
    // password link) so an admin can hand them to the new user in one action. The Copy
    // button reads the login/password live from the form, so it works with a generated or
    // a typed password.
    const credActions = `
        <div class="modal__field" style="flex-direction:row;gap:8px;flex-wrap:wrap">
            <button type="button" class="tbl-btn" id="uf-generate">${t('users.generate_password')}</button>
            <button type="button" class="tbl-btn copy-key-btn" id="uf-copy-creds">${t('users.copy_credentials')}</button>
        </div>`;
    // Telegram handle used to tag this operator in Clicker Bot messages — only on the
    // payouts that need a human (cancel / decline). Optional: with it empty the bot posts
    // exactly as it does today, just without the mention.
    const tgField = `
        <div class="modal__field">
            <label>${t('users.tg_username')} <span class="modal__hint">${t('users.tg_username_hint')}</span></label>
            <input id="uf-tg-username" type="text" autocomplete="off" placeholder="${t('users.tg_username_placeholder')}" value="${esc(user?.tg_username ?? '')}" />
        </div>`;
    body.innerHTML = `
        <form id="user-form" class="modal__form user-form" novalidate>
            ${credFields}
            ${credActions}
            ${tgField}
            <div class="modal__row">
                <div class="modal__field">
                    <label>${t('users.role')}</label>
                    ${roleControl}
                </div>
                <div class="modal__field modal__field--toggle">
                    <label>${t('users.active')}</label>
                    <label class="toggle">
                        <input id="uf-active" type="checkbox" ${user ? (user.active ? 'checked' : '') : 'checked'} />
                        <span class="toggle__track"></span>
                    </label>
                </div>
                ${lockControl}
            </div>
            ${groupControl}
            <div id="uf-error" class="modal__error" style="display:none"></div>
            <div class="modal__actions">
                <button type="button" class="modal__cancel" id="uf-cancel">${t('common.cancel')}</button>
                <button type="submit" class="modal__submit">${mode === 'new' ? t('common.create') : t('common.save')}</button>
            </div>
        </form>
    `;
    const form = body.querySelector('#user-form');
    body.querySelector('#uf-cancel').addEventListener('click', () => navigate({ page: 'users' }));
    // Generate password → fill both fields, which stay MASKED: the plaintext is never
    // shown, it leaves the panel only as the one-time link below. Copy credentials →
    // hand the password to the server, which stores it in SharePass and returns a
    // one-time link; the clipboard gets the panel URL + login + that link + the note
    // that it burns on first open and expires in a day.
    const loginInput = body.querySelector('#uf-login'); // absent in edit mode
    const pwInput = body.querySelector('#uf-password');
    const pw2Input = body.querySelector('#uf-password2');
    const genBtn = body.querySelector('#uf-generate');
    const copyBtn = body.querySelector('#uf-copy-creds');
    // Flash a transient label on a button, then restore its idle text.
    const flash = (btn, msg, idle) => {
        btn.textContent = msg;
        setTimeout(() => { btn.textContent = idle; }, 1500);
    };
    genBtn.addEventListener('click', () => {
        const pw = generatePassword();
        pwInput.value = pw;
        pw2Input.value = pw;
        // Nothing visibly changes in a masked field, so confirm on the button itself.
        flash(genBtn, t('users.password_generated'), t('users.generate_password'));
    });
    copyBtn.addEventListener('click', () => {
        const login = loginInput?.value.trim() || user?.login || '';
        const pw = pwInput.value;
        if (!login || !pw) {
            flash(copyBtn, t('users.set_login_password'), t('users.copy_credentials'));
            return;
        }
        copyBtn.disabled = true;
        copyBtn.textContent = t('users.creating_share_link');
        void (async () => {
            try {
                const res = await fetch('/api/users/share-password', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ password: pw }),
                });
                const data = await res.json();
                if (!res.ok || !data.url) {
                    flash(copyBtn, t('users.share_link_failed'), t('users.copy_credentials'));
                    return;
                }
                const creds = `${location.origin}\n`
                    + `${t('users.credentials_block', { login, link: data.url })}\n`
                    + t('users.credentials_link_note');
                await copyText(creds);
                flash(copyBtn, t('users.copied_check'), t('users.copy_credentials'));
            }
            catch {
                flash(copyBtn, t('users.share_link_failed'), t('users.copy_credentials'));
            }
            finally {
                copyBtn.disabled = false;
            }
        })();
    });
    // A superadmin is global and never belongs to a team: hide the «Team» field
    // when the selected role is superadmin, show it again for admin/operator.
    const roleSel = body.querySelector('#uf-role');
    const teamField = body.querySelector('#uf-team-field');
    roleSel.addEventListener('change', () => {
        if (teamField)
            teamField.style.display = roleSel.value === 'superadmin' ? 'none' : '';
    });
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const password = body.querySelector('#uf-password').value;
        const password2 = body.querySelector('#uf-password2').value;
        const roleVal = body.querySelector('#uf-role').value;
        const active = body.querySelector('#uf-active').checked;
        // role_locked is a superadmin-only field; the toggle only exists for a superadmin
        // actor, so read it when present and let the server ignore it for anyone else.
        const lockEl = body.querySelector('#uf-role-locked');
        const roleLocked = lockEl ? lockEl.checked : undefined;
        // Sent without the leading '@' (the server strips one anyway) — '' clears it.
        const tgUsername = body.querySelector('#uf-tg-username')
            .value.trim().replace(/^@/, '');
        const errorEl = body.querySelector('#uf-error');
        const btn = body.querySelector('.modal__submit');
        errorEl.style.display = 'none';
        // team_id is only ever sent by a superadmin (server ignores it for admins,
        // forcing their own team). null means "(no team)". A superadmin target is
        // global and never grouped → always submit null regardless of the picker.
        const teamSel = body.querySelector('#uf-team');
        const teamId = !isSuperadmin
            ? undefined
            : roleVal === 'superadmin'
                ? null
                : (teamSel && teamSel.value ? Number.parseInt(teamSel.value, 10) : null);
        // Only a superadmin may be without a team. A superadmin actor must pick one from
        // the list; an admin inherits their own team, so an ungrouped admin has nothing
        // to pass on — both are rejected by the server, so catch them here first.
        if (roleVal !== 'superadmin') {
            if (isSuperadmin && teamId === null) {
                showErr(errorEl, t('users.team_required'));
                return;
            }
            if (!isSuperadmin && mode === 'new' && !appState.currentUserTeamName) {
                showErr(errorEl, t('users.no_team_cannot_create'));
                return;
            }
        }
        if (mode === 'new') {
            const login = body.querySelector('#uf-login').value.trim();
            if (!login) {
                showErr(errorEl, t('users.login_required'));
                return;
            }
            if (!password) {
                showErr(errorEl, t('users.password_required'));
                return;
            }
            if (password !== password2) {
                showErr(errorEl, t('users.passwords_mismatch'));
                return;
            }
            btn.disabled = true;
            try {
                const createBody = { login, password, role: roleVal, active, tg_username: tgUsername };
                if (teamId !== undefined)
                    createBody['team_id'] = teamId;
                if (roleLocked !== undefined)
                    createBody['role_locked'] = roleLocked;
                const res = await fetch('/api/users', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(createBody),
                });
                const data = await res.json();
                if (!res.ok) {
                    showErr(errorEl, data.error ?? t('users.failed_to_create_user'));
                    return;
                }
                navigate({ page: 'users' });
            }
            catch {
                showErr(errorEl, t('users.network_error'));
            }
            finally {
                btn.disabled = false;
            }
            return;
        }
        // edit
        if (password && password !== password2) {
            showErr(errorEl, t('users.passwords_mismatch'));
            return;
        }
        const payload = { role: roleVal, active, tg_username: tgUsername };
        if (teamId !== undefined)
            payload['team_id'] = teamId;
        if (roleLocked !== undefined)
            payload['role_locked'] = roleLocked;
        if (password)
            payload['password'] = password;
        btn.disabled = true;
        try {
            const res = await fetch(`/api/users/${userId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (!res.ok) {
                showErr(errorEl, data.error ?? t('users.failed_to_update_user'));
                return;
            }
            navigate({ page: 'users' });
        }
        catch {
            showErr(errorEl, t('users.network_error'));
        }
        finally {
            btn.disabled = false;
        }
    });
}
/**
 * Copy `text` to the clipboard. The async Clipboard API is the normal path, but the
 * copy here happens AFTER an await (the SharePass round-trip), and some browsers only
 * honour a clipboard write inside the original user gesture — so fall back to the old
 * hidden-textarea + execCommand trick rather than silently copying nothing.
 */
async function copyText(text) {
    try {
        await navigator.clipboard.writeText(text);
        return;
    }
    catch { /* fall through */ }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    ta.remove();
}
/**
 * Generate a strong random password: `len` chars guaranteed to mix lower/upper/digits so
 * it passes typical strength checks, drawn with crypto for unbiased randomness. The
 * alphabet omits lookalike characters (0/O, 1/l/I) so a hand-copied password stays
 * unambiguous — same rationale as the DuoPlus share-code alphabet.
 */
function generatePassword(len = 16) {
    const lower = 'abcdefghijkmnpqrstuvwxyz';
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const digits = '23456789';
    const all = lower + upper + digits;
    const pick = (set, n) => {
        const buf = new Uint32Array(n);
        crypto.getRandomValues(buf);
        let s = '';
        for (let i = 0; i < n; i++)
            s += set[buf[i] % set.length];
        return s;
    };
    // One of each class guarantees the mix; fill the rest from the full alphabet, then
    // shuffle so the guaranteed chars aren't always in the first three positions.
    const arr = (pick(lower, 1) + pick(upper, 1) + pick(digits, 1) + pick(all, Math.max(0, len - 3))).split('');
    const rnd = new Uint32Array(arr.length);
    crypto.getRandomValues(rnd);
    for (let i = arr.length - 1; i > 0; i--) {
        const j = rnd[i] % (i + 1);
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr.join('');
}
